// PartnerConsentModal — Cluster F.2 / Q12.5 just-in-time modal.
//
// Mounted ONCE at the App root (next to <EntitlementModal>). Opens
// whenever any component awaits usePartnerConsentGate().check(...) and
// the backend's /partner-consents/check says `needs_consent: true`.
//
// UX:
//   - Radix Dialog primitive → focus trap, Escape close, ARIA labels.
//   - framer-motion AnimatePresence for view transitions (~180ms).
//   - One row per required consent_type with its checkbox. Submit
//     disabled until every required type is checked.
//   - Link to the partner's current_consent_text_url renders alongside.
//   - Brand-consistent: each view uses a partner-themed accent.
//   - Mobile-first sizing matches the C.6 RefundRequestModal pattern.
//
// Per Q12.5: "modal blocks booking; separate per-partner". The hook
// resolves false on dismiss so the booking page can short-circuit.
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Loader2,
  Shield,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  FileSignature,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PARTNER_VIEWS, usePartnerConsentModalApi } from '@/contexts/PartnerConsentContext';

const slideVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};
const slideTransition = { duration: 0.18, ease: 'easeOut' };

// Friendly labels per consent_type. Source enum:
// models_v01/partner.CONSENT_TYPES.
const CONSENT_TYPE_LABELS = {
  terms_and_privacy: {
    label: 'Terms of Service & Privacy Policy',
    description: 'Required to use the partner — covers how your data is processed.',
    accent: '#4A90E2',
  },
  data_sharing: {
    label: 'Data sharing with the partner',
    description: 'Share patient demographics + service details so the partner can fulfil your request.',
    accent: '#0EA5E9',
  },
  marketing: {
    label: 'Marketing communications (optional)',
    description: "Partner may email or message you about offers. Declining doesn't affect your booking.",
    accent: 'var(--color-consultation)',
  },
  partner_specific: {
    label: 'Partner-specific consent',
    description: 'Any additional consent this partner requires beyond the standard ones.',
    accent: '#D97706',
  },
};

export default function PartnerConsentModal() {
  const api = usePartnerConsentModalApi();
  const open = api.view !== PARTNER_VIEWS.CLOSED;
  const partner = api.checkResult?.partner;
  const required = api.checkResult?.required_consent_types || [];
  const missing = useMemo(() => api.checkResult?.missing_consent_types || [], [api.checkResult]);
  const accepted = api.checkResult?.accepted_consent_types || [];

  // Local check state. Required types are pre-disabled (must accept all);
  // optional types can stay unchecked. F.2 ships with no optional types
  // in the seeded data, but the modal supports them for future partners.
  const [checked, setChecked] = useState({});

  // Reset checks when the modal opens with new data.
  useEffect(() => {
    if (api.view === PARTNER_VIEWS.PROMPT) {
      const init = {};
      for (const ct of required) init[ct] = false;
      setChecked(init);
    }
  }, [api.view, partner?.partner_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Submit gating: every type in `missing` must be checked (the partner
  // can only block on REQUIRED types; if it ever introduces optional
  // ones, accepted[].length lets us treat already-accepted-on-current-
  // version ones as satisfied without re-asking).
  const canSubmit = useMemo(() => {
    if (api.view !== PARTNER_VIEWS.PROMPT) return false;
    return missing.every((ct) => checked[ct]);
  }, [api.view, missing, checked]);

  const handleSubmit = () => {
    // Only POST the types we just newly accepted to avoid double-writing
    // already-current ones.
    const toAccept = missing.filter((ct) => checked[ct]);
    api.submitAccept(toAccept);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) api.dismiss(); }}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogContent
          className="
            fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2
            w-[calc(100%-1.5rem)] max-w-md
            sm:rounded-2xl
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            duration-200
            p-0 overflow-hidden
            border-0 outline-none
            max-h-[90vh] overflow-y-auto
          "
          style={{ background: 'var(--surface)' }}
          data-testid="partner-consent-modal"
        >
          <DialogTitle className="sr-only">Partner consent required</DialogTitle>
          <DialogDescription className="sr-only">
            Review and accept the partner's terms before continuing with your booking
          </DialogDescription>

          <button
            type="button"
            onClick={() => api.dismiss()}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: 'var(--text-light)' }}
            data-testid="partner-consent-close"
          >
            <X size={18} />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={api.view}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="px-6 pt-8 pb-6"
              data-testid={`partner-consent-view-${api.view}`}
            >
              {api.view === PARTNER_VIEWS.LOADING && <LoadingView />}

              {api.view === PARTNER_VIEWS.PROMPT && partner && (
                <PromptView
                  partner={partner}
                  required={required}
                  missing={missing}
                  accepted={accepted}
                  checked={checked}
                  setChecked={setChecked}
                  canSubmit={canSubmit}
                  onSubmit={handleSubmit}
                  onDismiss={api.dismiss}
                />
              )}

              {api.view === PARTNER_VIEWS.SUBMITTING && <SubmittingView />}

              {api.view === PARTNER_VIEWS.ERROR && (
                <ErrorView
                  message={api.errorMessage}
                  onRetry={api.retry}
                  onDismiss={api.dismiss}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

// ─── Loading ───────────────────────────────────────────────────────────

function LoadingView() {
  return (
    <div className="px-2 py-10 flex flex-col items-center" data-testid="partner-consent-loading">
      <Loader2 size={28} className="animate-spin mb-3" style={{ color: 'var(--brand-primary)' }} />
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Checking partner consent…
      </p>
    </div>
  );
}

// ─── Prompt (the main form) ───────────────────────────────────────────

function PromptView({
  partner, required, missing, accepted, checked, setChecked,
  canSubmit, onSubmit, onDismiss,
}) {
  return (
    <div>
      {/* Header */}
      <div className="flex justify-center mb-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: '#4A90E21A' }}
        >
          <Shield size={26} style={{ color: '#4A90E2' }} aria-hidden="true" />
        </div>
      </div>
      <div className="text-center mb-2">
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: '#4A90E2', letterSpacing: '0.08em' }}>
          Partner consent
        </p>
        <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-main)' }}>
          {partner.display_name} needs your consent
        </h2>
      </div>
      {partner.description && (
        <p className="text-sm leading-relaxed text-center mb-4" style={{ color: 'var(--text-muted)' }}>
          {partner.description}
        </p>
      )}

      <p
        className="text-xs uppercase tracking-wider font-semibold mb-2"
        style={{ color: 'var(--text-muted)' }}
      >
        Please accept to continue
      </p>

      <div className="space-y-2 mb-4">
        {required.map((ct) => {
          const cfg = CONSENT_TYPE_LABELS[ct] || {
            label: ct.replace(/_/g, ' '),
            description: '',
            accent: '#6B7280',
          };
          const isAlreadyAccepted = accepted.includes(ct);
          return (
            <ConsentRow
              key={ct}
              label={cfg.label}
              description={cfg.description}
              accent={cfg.accent}
              checked={isAlreadyAccepted || !!checked[ct]}
              disabled={isAlreadyAccepted}
              alreadyAccepted={isAlreadyAccepted}
              onToggle={() => setChecked((s) => ({ ...s, [ct]: !s[ct] }))}
              testId={`partner-consent-row-${ct}`}
            />
          );
        })}
      </div>

      {/* T&C link with version pin */}
      <a
        href={partner.current_consent_text_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between rounded-xl p-3 mb-5 transition-colors hover:shadow-sm"
        style={{ background: 'var(--bg-main)', border: '1px solid var(--border-color)' }}
        data-testid="partner-consent-text-link"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: '#4A90E21A' }}
          >
            <FileSignature size={16} style={{ color: '#4A90E2' }} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-main)' }}>
              Read full terms
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
              {partner.current_consent_version}
            </p>
          </div>
        </div>
        <ExternalLink size={16} style={{ color: 'var(--text-light)' }} />
      </a>

      {/* Actions */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: '#4A90E2', color: 'white' }}
          data-testid="partner-consent-submit"
        >
          Accept & continue
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors border"
          style={{
            background: 'transparent',
            color: 'var(--text-base, var(--text-strong))',
            borderColor: 'var(--border-color, #e5e7eb)',
          }}
          data-testid="partner-consent-dismiss"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function ConsentRow({ label, description, accent, checked, disabled, alreadyAccepted, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      className="w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all"
      style={{
        background: checked ? `${accent}10` : 'var(--bg-main)',
        border: `2px solid ${checked ? accent : 'transparent'}`,
        cursor: disabled ? 'default' : 'pointer',
      }}
      data-testid={testId}
      aria-pressed={checked}
      disabled={disabled}
    >
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{
          background: checked ? accent : 'transparent',
          border: `2px solid ${checked ? accent : 'var(--border-color, #e5e7eb)'}`,
        }}
      >
        {checked && <CheckCircle2 size={14} style={{ color: 'white' }} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold" style={{ color: 'var(--text-main)' }}>
          {label}
          {alreadyAccepted && (
            <span
              className="ml-1.5 text-xs font-bold uppercase px-1.5 py-0.5 rounded-full"
              style={{ background: '#16A34A1A', color: '#16A34A' }}
            >
              Accepted
            </span>
          )}
        </p>
        {description && (
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {description}
          </p>
        )}
      </div>
    </button>
  );
}

// ─── Submitting ────────────────────────────────────────────────────────

function SubmittingView() {
  return (
    <div className="px-2 py-10 flex flex-col items-center" data-testid="partner-consent-submitting">
      <Loader2 size={28} className="animate-spin mb-3" style={{ color: 'var(--brand-primary)' }} />
      <p className="text-sm font-medium" style={{ color: 'var(--text-main)' }}>
        Recording your consent…
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
        Hold tight — this usually takes a second.
      </p>
    </div>
  );
}

// ─── Error ─────────────────────────────────────────────────────────────

function ErrorView({ message, onRetry, onDismiss }) {
  return (
    <div>
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: '#DC26261A' }}>
          <AlertTriangle size={26} style={{ color: '#DC2626' }} />
        </div>
      </div>
      <div className="text-center mb-2">
        <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-main)' }}>
          We hit a snag
        </h2>
      </div>
      <p className="text-sm leading-relaxed text-center mb-5" style={{ color: 'var(--text-muted)' }} data-testid="partner-consent-error-msg">
        {message || 'Could not load partner consent state. Please try again.'}
      </p>
      <div className="space-y-2">
        <button
          type="button"
          onClick={onRetry}
          className="w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] shadow-sm"
          style={{ background: '#DC2626', color: 'white' }}
          data-testid="partner-consent-retry"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors border"
          style={{
            background: 'transparent',
            color: 'var(--text-base, var(--text-strong))',
            borderColor: 'var(--border-color, #e5e7eb)',
          }}
          data-testid="partner-consent-error-close"
        >
          Close
        </button>
      </div>
    </div>
  );
}
